/**
 * 路線比較ダッシュボード (`RouteReportDashboardPage`) 専用の積み上げ横棒グラフ (2026-07-23 追加)。
 *
 * 従来は「100mあたり検出件数」を路線ごとに単色の棒 (`AreaBarChart` を density 1軸=1路線として
 * 流用) で表示していたが、「検出要素ごとに色分けしたい」というユーザー要望を受け、1本の棒を
 * カテゴリ (役割) ごとの色でスタックする表示に変更した。`AreaComparisonBarChart.tsx`
 * (カテゴリを行、エリアを棒として並べる) とは逆の軸取り — 本チャートは路線を行、カテゴリを
 * 棒の内訳とする。カテゴリの色は保存時点の `RouteReportSnapshot.axisCounts[].color`
 * (`computeAxisCounts.ts` が地区のマスタから動的に決めた色) をそのまま使うため、地図・
 * 「役割ごとの検出要素数」等の他表示と色が一致する。
 */

export interface RouteDensityBarChartSegment {
  key: string;
  label: string;
  color: string;
  /** 100mあたりの検出件数 (このカテゴリぶんのみ)。 */
  value: number;
}

export interface RouteDensityBarChartRow {
  key: string;
  /** 路線名。 */
  label: string;
  segments: RouteDensityBarChartSegment[];
}

export interface RouteDensityBarChartProps {
  rows: RouteDensityBarChartRow[];
}

export function RouteDensityBarChart({ rows }: RouteDensityBarChartProps) {
  if (rows.length === 0) {
    return <p className="text-sm text-slate-400">比較できるデータがありません。</p>;
  }

  const totals = rows.map((r) => r.segments.reduce((s, seg) => s + seg.value, 0));
  const maxTotal = Math.max(1, ...totals);

  // 凡例: 選択した路線群に登場する全カテゴリの和集合。合計値が大きい順に並べる。
  const legendByKey = new Map<string, { label: string; color: string; total: number }>();
  for (const r of rows) {
    for (const seg of r.segments) {
      const entry = legendByKey.get(seg.key);
      if (entry) entry.total += seg.value;
      else legendByKey.set(seg.key, { label: seg.label, color: seg.color, total: seg.value });
    }
  }
  const legend = [...legendByKey.entries()].sort((a, b) => b[1].total - a[1].total);

  return (
    <div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mb-3 text-xs text-slate-600">
        {legend.map(([key, v]) => (
          <span key={key} className="flex items-center gap-1.5">
            <span
              className="w-2.5 h-2.5 rounded-full shrink-0"
              style={{ backgroundColor: v.color }}
            />
            {v.label}
          </span>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-2">
        {rows.map((r, i) => (
          <div key={r.key} className="flex items-center gap-2 text-sm">
            <span className="w-40 shrink-0 text-slate-700 truncate">{r.label}</span>
            <span className="flex-1 h-4 rounded bg-slate-100 overflow-hidden flex">
              {r.segments
                .filter((seg) => seg.value > 0)
                .map((seg) => (
                  <span
                    key={seg.key}
                    className="block h-full"
                    style={{
                      width: `${(seg.value / maxTotal) * 100}%`,
                      backgroundColor: seg.color,
                    }}
                    title={`${seg.label}: ${seg.value.toFixed(1)}`}
                  />
                ))}
            </span>
            <span className="w-10 text-right font-semibold text-slate-700 tabular-nums shrink-0">
              {(totals[i] ?? 0).toFixed(1)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
